import React from "react";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";

/**
 * One drag context for the whole form, because **one gesture crosses two sections**: a media card
 * dropped on an entity card attaches it, dropped on another media card it moves there (decision 44).
 * dnd-kit only pairs a draggable with a droppable inside the same context, so the context has to sit
 * above both.
 *
 * It is a named provider rather than a `DndContext` inline in the page because the sections now
 * **require** it: `useDndMonitor` throws outside one, the same way `useBulletinRow` throws outside
 * `BulletinRowProvider`. A test that renders one section wraps it in this, and gets the real sensors
 * rather than a hand-rolled context that behaves differently.
 *
 * Neither section owns the drop. Each subscribes with `useDndMonitor` and handles the drags that
 * *start* in it, and `model/dropIntent.ts` is the one place that turns two ids into a decision.
 *
 * 8px of movement before a drag starts, so a handle can still be clicked and focused, and a touch
 * scroll that begins on one is still a scroll. Same constraint as `../add/`'s `SortableAttachments`,
 * the portal's existing sortable list. The `TouchSensor` is the whole reason media moved off HTML5
 * drag-and-drop, which fires nothing for touch; the `KeyboardSensor` is what makes both lists
 * reorderable without a pointer at all.
 */
export const BulletinDragProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}>
      {children}
    </DndContext>
  );
};
